import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Fish, Anchor, MapPin, ShoppingCart, ChefHat, Waves } from 'lucide-react';

// ---------------------------------------------------------------------------
// Data: 25+ fish species with seasonal availability
// ---------------------------------------------------------------------------

interface FishSpecies {
  name: string;
  /** 1-indexed months where this fish is at peak quality */
  peak: number[];
  /** 1-indexed months where this fish is available (includes peak) */
  available: number[];
  zone: string;
  type: 'blanco' | 'azul' | 'plano' | 'piscifactoria';
}

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const MONTH_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const FISH_DATA: FishSpecies[] = [
  { name: 'Merluza', peak: [1, 2, 3, 10, 11, 12], available: [1, 2, 3, 4, 5, 9, 10, 11, 12], zone: 'Cantabrico', type: 'blanco' },
  { name: 'Lubina', peak: [9, 10, 11, 12], available: [1, 2, 3, 9, 10, 11, 12], zone: 'Atlantico', type: 'blanco' },
  { name: 'Dorada', peak: [3, 4, 5, 10, 11], available: [3, 4, 5, 6, 9, 10, 11, 12], zone: 'Mediterraneo', type: 'blanco' },
  { name: 'Salmon (piscifactoria)', peak: [1,2,3,4,5,6,7,8,9,10,11,12], available: [1,2,3,4,5,6,7,8,9,10,11,12], zone: 'Piscifactoria', type: 'piscifactoria' },
  { name: 'Bacalao', peak: [11, 12, 1, 2], available: [1, 2, 3, 10, 11, 12], zone: 'Cantabrico', type: 'blanco' },
  { name: 'Rape', peak: [10, 11, 12, 1, 2], available: [1, 2, 3, 9, 10, 11, 12], zone: 'Cantabrico', type: 'blanco' },
  { name: 'Rodaballo', peak: [11, 12, 1, 2, 3], available: [1, 2, 3, 4, 10, 11, 12], zone: 'Atlantico', type: 'plano' },
  { name: 'Lenguado', peak: [1, 2, 3, 11, 12], available: [1, 2, 3, 4, 10, 11, 12], zone: 'Atlantico', type: 'plano' },
  { name: 'Atun rojo', peak: [5, 6, 7], available: [5, 6, 7, 8], zone: 'Atlantico', type: 'azul' },
  { name: 'Bonito del norte', peak: [7, 8, 9], available: [6, 7, 8, 9, 10], zone: 'Cantabrico', type: 'azul' },
  { name: 'Sardina', peak: [5, 6, 7, 8], available: [3, 4, 5, 6, 7, 8, 9], zone: 'Cantabrico', type: 'azul' },
  { name: 'Boqueron / Anchoa', peak: [3, 4, 5, 6], available: [3, 4, 5, 6, 7, 8], zone: 'Cantabrico', type: 'azul' },
  { name: 'Caballa', peak: [3, 4, 5, 8, 9], available: [3, 4, 5, 6, 7, 8, 9], zone: 'Cantabrico', type: 'azul' },
  { name: 'Jurel', peak: [6, 7, 8, 9], available: [5, 6, 7, 8, 9, 10], zone: 'Atlantico', type: 'azul' },
  { name: 'Pez espada', peak: [6, 7, 8], available: [5, 6, 7, 8, 9], zone: 'Mediterraneo', type: 'azul' },
  { name: 'Salmonete', peak: [3, 4, 5, 9, 10], available: [3, 4, 5, 6, 9, 10, 11], zone: 'Mediterraneo', type: 'blanco' },
  { name: 'Besugo', peak: [11, 12, 1, 2], available: [10, 11, 12, 1, 2, 3], zone: 'Cantabrico', type: 'blanco' },
  { name: 'Corvina', peak: [5, 6, 7, 8], available: [4, 5, 6, 7, 8, 9], zone: 'Atlantico', type: 'blanco' },
  { name: 'Mero', peak: [6, 7, 8, 9], available: [5, 6, 7, 8, 9, 10], zone: 'Mediterraneo', type: 'blanco' },
  { name: 'Congrio', peak: [10, 11, 12, 1], available: [9, 10, 11, 12, 1, 2], zone: 'Atlantico', type: 'blanco' },
  { name: 'Palometa', peak: [9, 10, 11], available: [8, 9, 10, 11, 12], zone: 'Atlantico', type: 'azul' },
  { name: 'Trucha', peak: [3, 4, 5, 6], available: [2, 3, 4, 5, 6, 7], zone: 'Rios', type: 'blanco' },
  { name: 'Chicharro', peak: [6, 7, 8], available: [5, 6, 7, 8, 9], zone: 'Cantabrico', type: 'azul' },
  { name: 'Cabracho', peak: [9, 10, 11], available: [8, 9, 10, 11, 12], zone: 'Cantabrico', type: 'blanco' },
  { name: 'Pargo', peak: [5, 6, 7], available: [4, 5, 6, 7, 8], zone: 'Canarias', type: 'blanco' },
];

// ---------------------------------------------------------------------------
// Seasons
// ---------------------------------------------------------------------------

interface SeasonInfo {
  name: string;
  months: string;
  color: string;
  bg: string;
  species: string[];
  description: string;
}

const SEASONS: SeasonInfo[] = [
  {
    name: 'Primavera',
    months: 'Marzo - Mayo',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200',
    species: ['Boqueron / Anchoa', 'Sardina', 'Salmonete', 'Caballa', 'Trucha', 'Dorada', 'Pargo'],
    description: 'La primavera trae los primeros pescados azules con fuerza: el boqueron esta en su mejor momento para vinagre, la sardina empieza a engrasar y el salmonete de roca ofrece un producto gourmet a precio razonable. Es el momento de introducir ceviches, escabeches y frituras ligeras en la carta.',
  },
  {
    name: 'Verano',
    months: 'Junio - Agosto',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    species: ['Atun rojo', 'Bonito del norte', 'Sardina', 'Jurel', 'Pez espada', 'Corvina', 'Mero', 'Chicharro'],
    description: 'El verano es la epoca dorada de los pescados azules. El atun rojo de almadraba y el bonito del norte cantabrico son los reyes del mar. La sardina a la brasa es el plato estrella de las terrazas. El pez espada y la corvina ofrecen opciones premium para plancha y horno.',
  },
  {
    name: 'Otono',
    months: 'Septiembre - Noviembre',
    color: 'text-orange-700',
    bg: 'bg-orange-50 border-orange-200',
    species: ['Besugo', 'Rape', 'Lubina', 'Cabracho', 'Palometa', 'Congrio', 'Salmonete'],
    description: 'El otono marca la vuelta de los grandes pescados blancos. El rape y la lubina recuperan protagonismo, el cabracho permite caldos y sopas con sabor intenso, y el besugo empieza a llenar las lonjas. Es la estacion perfecta para guisos marineros y platos de cuchara.',
  },
  {
    name: 'Invierno',
    months: 'Diciembre - Febrero',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    species: ['Merluza', 'Bacalao', 'Besugo', 'Rodaballo', 'Lenguado', 'Rape', 'Congrio'],
    description: 'El invierno ofrece los pescados blancos en su maximo esplendor. La merluza de pincho cantabrica, el bacalao fresco, el rodaballo y el lenguado alcanzan su mejor calidad. El besugo es tradicion navidena imprescindible. Ideal para hornos, caldos y preparaciones clasicas.',
  },
];

// ---------------------------------------------------------------------------
// Price comparison data
// ---------------------------------------------------------------------------

interface PriceComparison {
  fish: string;
  inSeason: string;
  offSeason: string;
  saving: string;
}

const PRICE_COMPARISONS: PriceComparison[] = [
  { fish: 'Sardina', inSeason: '3-5 EUR/kg', offSeason: '7-9 EUR/kg', saving: '~50%' },
  { fish: 'Boqueron', inSeason: '4-6 EUR/kg', offSeason: '8-11 EUR/kg', saving: '~45%' },
  { fish: 'Bonito del norte', inSeason: '8-12 EUR/kg', offSeason: '15-20 EUR/kg', saving: '~40%' },
  { fish: 'Besugo', inSeason: '12-18 EUR/kg', offSeason: '25-35 EUR/kg', saving: '~45%' },
  { fish: 'Merluza (pincho)', inSeason: '10-15 EUR/kg', offSeason: '18-25 EUR/kg', saving: '~35%' },
  { fish: 'Dorada salvaje', inSeason: '8-12 EUR/kg', offSeason: '14-20 EUR/kg', saving: '~35%' },
];

// ---------------------------------------------------------------------------
// Fishing zones
// ---------------------------------------------------------------------------

interface FishingZone {
  name: string;
  description: string;
  species: string[];
  icon: string;
}

const ZONES: FishingZone[] = [
  {
    name: 'Cantabrico',
    description: 'Desde Galicia hasta el Pais Vasco. Aguas frias y ricas en nutrientes que producen los mejores pescados blancos y azules de Espana. Merluza de pincho, bonito del norte, anchoa de Santoña y besugo son sus estrellas.',
    species: ['Merluza', 'Bonito del norte', 'Anchoa', 'Besugo', 'Cabracho', 'Rape'],
    icon: '🌊',
  },
  {
    name: 'Atlantico norte (Galicia)',
    description: 'Las rias gallegas y la costa de Fisterra ofrecen una biodiversidad marina excepcional. Lubina, rodaballo y congrio se pescan con artes tradicionales. Ademas del marisco, Galicia es referencia en pescado de calidad.',
    species: ['Lubina', 'Rodaballo', 'Congrio', 'Jurel', 'Lenguado'],
    icon: '🐟',
  },
  {
    name: 'Mediterraneo',
    description: 'Desde Catalunya hasta Andalucia oriental. Aguas mas calidas con especies como la dorada, el salmonete, el mero y el pez espada. La tradicion pesquera mediterranea aporta tecnicas unicas como la almadraba.',
    species: ['Dorada', 'Salmonete', 'Mero', 'Pez espada', 'Atun rojo'],
    icon: '☀️',
  },
  {
    name: 'Canarias',
    description: 'Aguas subtropicales con especies unicas como el pargo, la vieja y el cherne. La corriente de Canarias aporta nutrientes que sostienen una pesca artesanal de alta calidad con sabores que no se encuentran en la peninsula.',
    species: ['Pargo', 'Vieja', 'Cherne', 'Atun'],
    icon: '🏝️',
  },
];

// ---------------------------------------------------------------------------
// FAQs
// ---------------------------------------------------------------------------

const faqs = [
  {
    q: '¿Que pescado esta de temporada ahora en Espana?',
    a: 'Depende del mes. En general, los pescados azules (sardina, boqueron, bonito, atun rojo) estan en temporada en primavera-verano (marzo-septiembre). Los pescados blancos (merluza, bacalao, rape, besugo) tienen su mejor momento en otono-invierno (octubre-marzo). Consulta nuestro calendario mensual para ver el detalle exacto de cada especie.',
  },
  {
    q: '¿Por que es mejor comprar pescado de temporada?',
    a: 'Hay tres razones clave: 1) Precio — el pescado en temporada puede costar entre un 30% y un 50% menos. 2) Calidad — el pescado en su epoca optima tiene mas grasa, mejor textura y sabor mas intenso. 3) Sostenibilidad — respetar los ciclos naturales ayuda a mantener las poblaciones de peces. Para un restaurante, esto se traduce en menor food cost y mejor producto.',
  },
  {
    q: '¿Cuando es la temporada de atun rojo en Espana?',
    a: 'La temporada del atun rojo de almadraba en Espana va de mayo a julio, con el pico en junio. Las almadrabas de Barbate, Zahara de los Atunes y Conil (Cadiz) capturan el atun rojo que migra del Atlantico al Mediterraneo para desovar. Es uno de los productos mas preciados de la gastronomia espanola.',
  },
  {
    q: '¿Cual es el pescado mas barato de temporada?',
    a: 'Los pescados azules en temporada son los mas economicos: la sardina (3-5 EUR/kg en verano), el boqueron (4-6 EUR/kg en primavera), la caballa (3-5 EUR/kg) y el jurel (3-5 EUR/kg). Ofrecen una excelente relacion calidad-precio y son muy versatiles en cocina.',
  },
  {
    q: '¿Como se si un pescado es fresco?',
    a: 'Los indicadores clave son: 1) Ojos brillantes, transparentes y abultados (nunca hundidos u opacos). 2) Agallas de color rojo vivo o rosa intenso. 3) Carne firme y elastica — al presionar con el dedo debe recuperar la forma. 4) Olor a mar limpio, nunca a amoniaco. 5) Piel brillante y escamas bien adheridas.',
  },
  {
    q: '¿Que diferencia hay entre pescado blanco y azul?',
    a: 'El pescado azul (sardina, boqueron, bonito, atun, caballa) tiene mas de un 5% de grasa y es mas sabroso e intenso. El pescado blanco (merluza, rape, lenguado, rodaballo) tiene menos de un 2% de grasa, con sabor mas suave y textura delicada. La diferencia afecta al cocinado: los azules van mejor a la brasa o en escabeche, los blancos al horno o en salsa.',
  },
  {
    q: '¿Como afecta la temporada del pescado al food cost de mi restaurante?',
    a: 'Enormemente. Un restaurante que adapta su carta al pescado de temporada puede reducir el food cost en pescados entre un 25% y un 45%. Por ejemplo, ofrecer sardina en junio en vez de en diciembre puede ahorrar un 50% en materia prima. Con PLEKO puedes calcular el escandallo de cada plato y ver en tiempo real como la estacionalidad impacta en tu rentabilidad.',
  },
  {
    q: '¿Que pescado se puede comer todo el ano?',
    a: 'El salmon de piscifactoria esta disponible todo el ano con calidad constante. La merluza, aunque tiene mejor temporada en invierno, se encuentra disponible casi todo el ano gracias a las distintas zonas de pesca. La dorada y lubina de acuicultura tambien estan disponibles siempre, aunque su sabor y textura son inferiores al salvaje de temporada.',
  },
];

// ---------------------------------------------------------------------------
// JSON-LD structured data
// ---------------------------------------------------------------------------

const jsonLdFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Calendario temporada', item: 'https://pleko.app/calendario-temporada' },
    { '@type': 'ListItem', position: 3, name: 'Pescado de temporada', item: 'https://pleko.app/pescado-de-temporada' },
  ],
};

const jsonLdArticle = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Pescado de Temporada en Espana: Calendario Completo Mes a Mes',
  description: 'Calendario completo de pescado de temporada en Espana con 25+ especies, precios orientativos, zonas de pesca y consejos para restaurantes.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  url: 'https://pleko.app/pescado-de-temporada',
};

// ---------------------------------------------------------------------------
// Tips for buying fresh fish
// ---------------------------------------------------------------------------

const FRESHNESS_TIPS = [
  { title: 'Ojos', desc: 'Brillantes, transparentes y abultados. Si estan hundidos u opacos, el pescado no es fresco.', icon: '👁️' },
  { title: 'Agallas', desc: 'Color rojo vivo o rosa intenso. Si son marrones o grises, ha pasado demasiado tiempo desde la captura.', icon: '🔴' },
  { title: 'Textura', desc: 'Carne firme y elastica. Al presionar con el dedo debe recuperar su forma inmediatamente.', icon: '🤏' },
  { title: 'Olor', desc: 'A mar limpio y fresco. Nunca a amoniaco, acido o desagradable. El pescado fresco apenas huele.', icon: '👃' },
  { title: 'Piel', desc: 'Brillante, humeda y con escamas bien adheridas. Si las escamas se caen facilmente, no es fresco.', icon: '✨' },
  { title: 'Rigidez', desc: 'Un pescado fresco esta rigido. Si al cogerlo por la cabeza el cuerpo cae fláccido, descartalo.', icon: '🐟' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PescadoTemporadaPage() {
  const [highlightMonth, setHighlightMonth] = useState<number | null>(null);
  const currentMonth = new Date().getMonth() + 1;

  return (
    <>
      <Helmet>
        <title>Pescado de Temporada en Espana 2026: Calendario Completo | PLEKO</title>
        <meta
          name="description"
          content="Calendario completo de pescado de temporada en Espana mes a mes. 25+ especies con estacionalidad, precios orientativos, zonas de pesca y consejos para restaurantes. Optimiza tu carta y reduce el food cost."
        />
        <meta
          name="keywords"
          content="pescado de temporada, pescado temporada espana, calendario pescado temporada, que pescado esta de temporada, pescado de temporada hoy, pescado azul temporada, merluza temporada, atun rojo temporada, bonito temporada, sardina temporada"
        />
        <link rel="canonical" href="https://pleko.app/pescado-de-temporada" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/pescado-de-temporada" />
        <meta property="og:title" content="Pescado de Temporada en Espana 2026: Calendario Completo | PLEKO" />
        <meta
          property="og:description"
          content="Calendario completo de pescado de temporada en Espana. 25+ especies, precios y consejos para restaurantes."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pescado de Temporada en Espana 2026: Calendario Completo | PLEKO" />
        <meta name="twitter:description" content="Calendario completo de pescado de temporada en Espana. 25+ especies, precios y consejos para restaurantes." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify([jsonLdFaq, jsonLdBreadcrumb, jsonLdArticle])}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Fish className="w-4 h-4" />
                Calendario de pescado de temporada en Espana — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Pescado de <span className="text-blue-600">Temporada</span> en Espana: Calendario Completo Mes a Mes
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
                Descubre que pescado esta en su mejor momento cada mes del ano. Calendario con 25+ especies,
                precios orientativos, zonas de pesca y consejos para optimizar la carta de tu restaurante.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="#calendario" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors">
                  Ver calendario <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#estaciones" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 font-semibold rounded-xl border border-blue-200 hover:bg-blue-50 transition-colors">
                  Por estaciones
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why buy seasonal fish */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              ¿Por que comprar pescado de temporada?
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Tres razones de peso para que tu restaurante trabaje con pescado en su momento optimo.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Mejor precio',
                  desc: 'El pescado en temporada puede costar entre un 30% y un 50% menos que fuera de ella. Mas producto en lonja = precios mas competitivos para tu restaurante.',
                  color: 'bg-green-100 text-green-600',
                  icon: ShoppingCart,
                },
                {
                  title: 'Maxima calidad',
                  desc: 'Un pescado en su epoca optima tiene mas grasa, mejor textura y sabor mas intenso. Tus clientes lo notan en cada bocado y vuelven.',
                  color: 'bg-blue-100 text-blue-600',
                  icon: ChefHat,
                },
                {
                  title: 'Sostenibilidad',
                  desc: 'Respetar los ciclos naturales permite la regeneracion de las poblaciones de peces. Es un argumento de venta cada vez mas valorado por los comensales.',
                  color: 'bg-cyan-100 text-cyan-600',
                  icon: Waves,
                },
              ].map((b, i) => (
                <div key={i} className="text-center">
                  <div className={`w-16 h-16 ${b.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <b.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-slate-600">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MAIN CALENDAR TABLE */}
        <section id="calendario" className="bg-slate-50 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Calendario anual de pescado de temporada
            </h2>
            <p className="text-slate-600 text-center mb-3 max-w-2xl mx-auto">
              25 especies de pescado con su disponibilidad mes a mes. Pasa el cursor sobre un mes para resaltarlo.
            </p>
            <div className="flex items-center justify-center gap-6 mb-8 text-sm">
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-5 rounded bg-blue-600" /> Temporada alta (pico)
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-5 rounded bg-blue-200" /> Disponible
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-5 rounded bg-slate-100" /> Fuera de temporada
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[800px] w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-slate-700 font-semibold w-48 bg-white sticky left-0 z-10">
                      Especie
                    </th>
                    {MONTH_NAMES.map((name, i) => {
                      const m = i + 1;
                      const isCurrentMonth = m === currentMonth;
                      const isHighlighted = m === highlightMonth;
                      return (
                        <th
                          key={i}
                          className={`text-center py-3 px-1 text-xs font-semibold cursor-pointer transition-colors ${
                            isHighlighted
                              ? 'bg-blue-100 text-blue-800'
                              : isCurrentMonth
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-slate-500'
                          }`}
                          onMouseEnter={() => setHighlightMonth(m)}
                          onMouseLeave={() => setHighlightMonth(null)}
                        >
                          {name}
                        </th>
                      );
                    })}
                    <th className="text-center py-3 px-2 text-xs font-semibold text-slate-500">Zona</th>
                  </tr>
                </thead>
                <tbody>
                  {FISH_DATA.map((fish, idx) => (
                    <tr key={fish.name} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className={`py-2 px-4 font-medium text-slate-800 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                        <span className="flex items-center gap-1.5">
                          <Fish className={`w-3.5 h-3.5 ${fish.type === 'azul' ? 'text-blue-500' : fish.type === 'plano' ? 'text-teal-500' : fish.type === 'piscifactoria' ? 'text-slate-400' : 'text-cyan-500'}`} />
                          {fish.name}
                        </span>
                      </td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const m = i + 1;
                        const isPeak = fish.peak.includes(m);
                        const isAvail = fish.available.includes(m);
                        const isHighlighted = m === highlightMonth;
                        return (
                          <td
                            key={m}
                            className={`text-center py-2 px-1 transition-colors ${isHighlighted ? 'ring-1 ring-inset ring-blue-300' : ''}`}
                            onMouseEnter={() => setHighlightMonth(m)}
                            onMouseLeave={() => setHighlightMonth(null)}
                          >
                            {isPeak ? (
                              <span className="inline-block w-6 h-4 rounded bg-blue-600" />
                            ) : isAvail ? (
                              <span className="inline-block w-6 h-4 rounded bg-blue-200" />
                            ) : (
                              <span className="inline-block w-6 h-4 rounded bg-slate-100" />
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center py-2 px-2 text-xs text-slate-500">{fish.zone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">
              * Datos orientativos basados en las principales lonjas de Espana. La disponibilidad puede variar segun la region y el ano.
              El salmon de piscifactoria esta disponible todo el ano con calidad constante.
            </p>
          </div>
        </section>

        {/* By season */}
        <section id="estaciones" className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Pescado de temporada por estacion
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Cada estacion trae pescados con caracteristicas unicas. Adapta tu carta para sacar el maximo partido.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {SEASONS.map((season) => (
                <div key={season.name} className={`rounded-xl border p-6 ${season.bg}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`text-xl font-bold ${season.color}`}>{season.name}</h3>
                    <span className="text-sm text-slate-500">({season.months})</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">{season.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {season.species.map((sp) => (
                      <span key={sp} className="px-3 py-1 bg-white/70 rounded-full text-sm font-medium text-slate-700 border border-slate-200">
                        {sp}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Price comparison table */}
        <section className="bg-blue-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Precios orientativos: temporada vs fuera de temporada
            </h2>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              La diferencia de precio entre comprar en temporada y fuera de ella puede superar el 40%.
              Estos precios son orientativos basados en lonjas espanolas.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl border border-slate-200 overflow-hidden text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Pescado</th>
                    <th className="text-center py-3 px-4 font-semibold text-green-700">En temporada</th>
                    <th className="text-center py-3 px-4 font-semibold text-red-700">Fuera de temporada</th>
                    <th className="text-center py-3 px-4 font-semibold text-blue-700">Ahorro</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_COMPARISONS.map((pc, idx) => (
                    <tr key={pc.fish} className={`border-t border-slate-100 ${idx % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                      <td className="py-3 px-4 font-medium text-slate-800">{pc.fish}</td>
                      <td className="py-3 px-4 text-center text-green-700 font-medium">{pc.inSeason}</td>
                      <td className="py-3 px-4 text-center text-red-600">{pc.offSeason}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-semibold text-xs">{pc.saving}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              * Precios orientativos en EUR/kg basados en lonjas de Mercabarna y Mercamadrid (2025-2026).
              Los precios reales pueden variar segun proveedor, calidad y region.
            </p>
          </div>
        </section>

        {/* How to integrate seasonal fish in your menu */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Como integrar pescado de temporada en tu carta
            </h2>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              Consejos practicos para rotar el pescado de tu carta segun la estacion y maximizar la rentabilidad.
            </p>
            <div className="space-y-6">
              {[
                {
                  title: 'Rota la carta cada trimestre',
                  desc: 'Cambia las opciones de pescado de tu carta al menos 4 veces al ano, coincidiendo con las estaciones. Tus clientes perciben frescura y profesionalidad, y tu reduces el food cost al trabajar con producto de temporada.',
                },
                {
                  title: 'Crea una seccion de "pizarra del dia"',
                  desc: 'Reserva una seccion de la carta (pizarra, menu del dia, sugerencia del chef) para el pescado del dia segun lo que llegue fresco de lonja. Permite flexibilidad y transmite autenticidad.',
                },
                {
                  title: 'Calcula el escandallo de cada pescado',
                  desc: 'Un mismo plato puede variar un 30-40% en food cost segun la temporada del pescado. Calcula el escandallo con precios actualizados para cada rotacion de carta y ajusta el PVP si es necesario.',
                },
                {
                  title: 'Comunica la temporada a tus clientes',
                  desc: 'Indica en la carta que trabajas con pescado de temporada. Los comensales valoran cada vez mas la sostenibilidad y el producto local. Es un argumento comercial potente.',
                },
                {
                  title: 'Combina pescado de temporada con guarniciones estacionales',
                  desc: 'Un besugo de invierno con alcachofas de temporada o una sardina de verano con tomate raf tiene mas sentido gastronomico y comercial que combinar productos de epocas distintas.',
                },
              ].map((tip, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Consejo PLEKO:</strong> Usa la{' '}
                <Link to="/ingenieria-de-menu" className="underline hover:text-blue-600">ingenieria de menu</Link>
                {' '}para analizar que platos de pescado son los mas rentables en cada estacion y toma decisiones basadas en datos.
              </p>
            </div>
          </div>
        </section>

        {/* Fishing zones in Spain */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-10">
              <MapPin className="w-7 h-7 text-blue-600" />
              <h2 className="text-3xl font-bold text-slate-900">
                Zonas de pesca en Espana
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {ZONES.map((zone) => (
                <div key={zone.name} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{zone.icon}</span>
                    <h3 className="text-lg font-bold text-slate-900">{zone.name}</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">{zone.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {zone.species.map((sp) => (
                      <span key={sp} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {sp}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fresh fish buying tips */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-10">
              <Anchor className="w-7 h-7 text-blue-600" />
              <h2 className="text-3xl font-bold text-slate-900">
                Consejos para comprar pescado fresco
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FRESHNESS_TIPS.map((tip) => (
                <div key={tip.title} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-200 hover:shadow-sm transition-all">
                  <span className="text-3xl mb-3 block">{tip.icon}</span>
                  <h3 className="font-semibold text-slate-900 mb-2">{tip.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What fish is in season NOW */}
        <section className="bg-blue-600 py-16 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6">
              Pescado de temporada en {MONTH_FULL[currentMonth - 1]} {new Date().getFullYear()}
            </h2>
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              {FISH_DATA.filter((f) => f.peak.includes(currentMonth)).map((f) => (
                <span key={f.name} className="px-4 py-2 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {f.name}
                </span>
              ))}
            </div>
            <p className="text-blue-100 text-center text-sm max-w-xl mx-auto">
              Estas son las especies en su pico de calidad este mes. Tambien disponibles (fuera de pico):{' '}
              {FISH_DATA.filter((f) => f.available.includes(currentMonth) && !f.peak.includes(currentMonth)).map((f) => f.name).join(', ') || 'ninguna adicional'}.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
              Preguntas frecuentes sobre pescado de temporada
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer p-5 font-semibold text-slate-900 hover:bg-slate-50">
                    {faq.q}
                    <span className="ml-4 text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Guias relacionadas
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {[
                { to: '/calendario-temporada', label: 'Calendario de temporada', emoji: '📅' },
                { to: '/tabla-mermas-alimentos', label: 'Tabla de mermas', emoji: '📊' },
                { to: '/escandallo', label: 'Que es un escandallo', emoji: '📋' },
                { to: '/gestion-compras-restaurante', label: 'Gestion de compras', emoji: '🛒' },
                { to: '/precios-mercado', label: 'Precios de mercado', emoji: '💰' },
                { to: '/ingenieria-de-menu', label: 'Ingenieria de menu', emoji: '🍽️' },
                { to: '/food-cost', label: 'Food cost', emoji: '📈' },
                { to: '/control-costes-restaurante', label: 'Control de costes', emoji: '🎯' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-sm font-medium text-slate-700"
                >
                  <span>{link.emoji}</span>
                  <span>{link.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-400" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-cyan-600 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Planifica tu carta con productos de temporada y controla costes
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
              Con PLEKO calculas el escandallo de cada plato de pescado en segundos, comparas precios
              de mercado y optimizas tu carta para maximizar la rentabilidad.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-700 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
            >
              Prueba gratis 14 dias <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
